// src/pages/playlists/playlistsStore.js
// One shared copy of the signed-in user's playlists (summaries), used by the
// playlist pages and every save button. Writes are optimistic: state changes
// first, then the request; a failure rolls back and rethrows for a toast.
import { useEffect, useSyncExternalStore } from "react";
import { useAuth } from "../../hooks/useAuth";
import {
  fetchPlaylists,
  createPlaylistReq,
  updatePlaylistReq,
  deletePlaylistReq,
  restorePlaylistReq,
  errorMessage,
} from "./playlistsApi";
import {
  replacePlaylist,
  prependPlaylist,
  removePlaylist,
  insertPlaylistAt,
  patchPlaylist,
  toggleKey,
  moveKey,
} from "./playlistsModel";

let state = { owner: null, status: "idle", playlists: [], error: "" };
const listeners = new Set();
// Latest write per playlist, so a slow older response can't overwrite a newer one.
const writeSeq = new Map();

function setState(patch) {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
}
const subscribe = (l) => {
  listeners.add(l);
  return () => listeners.delete(l);
};
const getSnapshot = () => state;
const find = (id) => state.playlists.find((p) => p.id === id);

export function loadPlaylists(owner) {
  const switching = state.owner !== owner;
  setState({ owner, status: "loading", error: "", ...(switching ? { playlists: [] } : {}) });
  return fetchPlaylists().then(
    (playlists) => {
      if (state.owner === owner) setState({ status: "ready", playlists });
    },
    (err) => {
      if (state.owner === owner) setState({ status: "error", error: errorMessage(err, "Couldn't load your playlists.") });
    },
  );
}

const ownerOf = (user) => user?._id || user?.id || user?.email || null;

// The signed-in user's playlists; loads them the first time anything asks.
export function useMyPlaylists() {
  const { user } = useAuth();
  const owner = ownerOf(user);
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  useEffect(() => {
    if (!owner) return;
    if (state.owner !== owner || state.status === "idle") loadPlaylists(owner);
  }, [owner]);
  if (!owner || snap.owner !== owner) return { owner, status: owner ? "loading" : "idle", playlists: [], error: "" };
  return { ...snap, owner };
}

function nextSeq(id) {
  const n = (writeSeq.get(id) || 0) + 1;
  writeSeq.set(id, n);
  return n;
}

export async function setItems(id, keys) {
  const prev = find(id)?.items || [];
  setState({ playlists: patchPlaylist(state.playlists, id, { items: keys }) });
  const seq = nextSeq(id);
  try {
    const fresh = await updatePlaylistReq(id, { items: keys });
    if (writeSeq.get(id) === seq) setState({ playlists: replacePlaylist(state.playlists, fresh) });
    return fresh;
  } catch (err) {
    if (writeSeq.get(id) === seq) setState({ playlists: patchPlaylist(state.playlists, id, { items: prev }) });
    throw err;
  }
}

export const toggleItem = (id, key) => setItems(id, toggleKey(find(id)?.items || [], key));
export const moveItem = (id, key, dir) => setItems(id, moveKey(find(id)?.items || [], key, dir));

export async function createPlaylist({ name, desc = "", items = [] }) {
  const fresh = await createPlaylistReq({ name, desc, items });
  setState({ playlists: prependPlaylist(state.playlists, fresh) });
  return fresh;
}

export async function updateMeta(id, { name, desc }) {
  const prev = find(id);
  setState({ playlists: patchPlaylist(state.playlists, id, { name, desc }) });
  const seq = nextSeq(id);
  try {
    const fresh = await updatePlaylistReq(id, { name, desc });
    if (writeSeq.get(id) === seq) setState({ playlists: replacePlaylist(state.playlists, fresh) });
    return fresh;
  } catch (err) {
    if (prev && writeSeq.get(id) === seq) setState({ playlists: replacePlaylist(state.playlists, prev) });
    throw err;
  }
}

// Returns what restorePlaylist needs for Undo.
export async function deletePlaylist(id) {
  const index = state.playlists.findIndex((p) => p.id === id);
  const snapshot = find(id);
  setState({ playlists: removePlaylist(state.playlists, id) });
  try {
    await deletePlaylistReq(id);
    return { snapshot, index };
  } catch (err) {
    if (snapshot) setState({ playlists: insertPlaylistAt(state.playlists, snapshot, index) });
    throw err;
  }
}

export async function restorePlaylist(snapshot, index) {
  const fresh = await restorePlaylistReq({
    id: snapshot.id,
    name: snapshot.name,
    desc: snapshot.desc,
    items: snapshot.items,
    createdAt: snapshot.createdAt,
    updatedAt: snapshot.updatedAt,
  });
  setState({ playlists: insertPlaylistAt(state.playlists, fresh, index) });
  return fresh;
}
